import React from "react";
import cx from "clsx";

import Node from "@/components/tour/node";
import ThreeSixty from "@/components/tour/threeSixty";
import StopPoint from "@/components/tour/stopPoint";
import { debounce } from "@/helpers";

import vars from "@/styles/_variables.scss";
import stopPointStyles from "@/components/tour/stopPoint.module.scss";
// import globalStyles from "@/styles/global.module.scss";
import styles from "./videoStopPoints.module.scss";

const NODES = {
  [-1]: [
    { top: -999, left: -999 },
    { top: -999, left: -999 },
    { top: -999, left: -999 },
    { top: -999, left: -999 }
  ],
  6: [
    {
      top: 36,
      left: 20,
      tooltip: "Large open floor plates with views of Santa Cruz mountains."
    },
    { top: 62, left: 40, tooltip: "Onsite childcare facility." },
    { top: 22, left: 56, tooltip: "Abundant outdoor terraces." },
    { top: -999, left: -999 }
  ],
  7: [
    {
      top: 40,
      left: 20,
      tooltip:
        "Exposed concrete ceilings and columns. Large floor plates with 100% fresh air circulation."
    },
    { top: 40, left: 42, tooltip: "Open collaboration and maker space." },
    { top: 36, left: 65, tooltip: "Abundant outdoor terraces." },
    { top: -999, left: -999 }
  ],
  8: [
    {
      top: 20,
      left: 64,
      tooltip: "Signature terrace and roof deck for outdoor activities."
    },
    {
      top: 36,
      left: 30,
      tooltip:
        "Concrete construction with full-height curtain wall glass and 100% fresh air supply."
    },
    {
      top: 66,
      left: 40,
      tooltip: "Cafe retail adjacent to the local dog agility park."
    },
    { top: -999, left: -999 }
  ],
  9: [
    {
      top: 30,
      left: 16,
      tooltip:
        "Unique architecture with large floor plates and concrete construction."
    },
    { top: 31, left: 58, tooltip: "Abundant outdoor terraces." },
    {
      top: 50,
      left: 50,
      tooltip: "Shed Park outdoor space for gathering and wellness."
    },
    { top: 45, left: 72, tooltip: "Craft eatery." }
  ]
};

const THREE_SIXTYS = {
  [-1]: [{ top: -999, left: -999, threeSixty: true, panoIndex: -1 }],
  6: [
    {
      top: 24,
      left: 70,
      threeSixty: true,
      panoIndex: 1,
      bldg: "e1",
      label: "The Nest"
    }
  ],
  7: [
    {
      top: 60,
      left: 45,
      threeSixty: true,
      panoIndex: 2,
      bldg: "e2",
      label: "The Shop"
    }
  ],
  8: [
    {
      top: 14,
      left: 50,
      threeSixty: true,
      panoIndex: 4,
      bldg: "e3",
      label: "The Loft"
    }
  ],
  9: [
    {
      top: 54,
      left: 24,
      threeSixty: true,
      panoIndex: 5,
      bldg: "e4",
      label: "The Mill"
    }
  ]
};

class VideoStopPoints extends React.PureComponent {
  state = {
    tempStopIndex: -1,
    hasMounted: false,
    pointsHeight: 0
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { isDesktop, windowHeight } = nextProps;
    const { hasMounted } = prevState;

    let pointsHeight = isDesktop
      ? windowHeight
      : windowHeight - parseInt(vars.tourBottomHeight) - 21;

    if (!hasMounted) pointsHeight = 0;

    return {
      ...prevState,
      pointsHeight
    };
  }

  componentDidMount() {
    const { stopIndex = -1 } = this.props;

    this.setState({ hasMounted: true, tempStopIndex: stopIndex });
  }

  componentDidUpdate(prevProps) {
    const { stopIndex = -1 } = this.props;

    // Keep record of prev stopIndex so out animation can finish
    if (stopIndex > -1 && stopIndex !== prevProps.stopIndex) {
      setTimeout(() => this.setState({ tempStopIndex: stopIndex }), 100);
    }
    if (prevProps.stopIndex && stopIndex === -1) {
      this.resetTempStopIndex();
    }
  }

  resetTempStopIndex = debounce(() => {
    if (this.props.stopIndex === -1) {
      this.setState({ tempStopIndex: -1 });
    }
  }, 330);

  handleStopClick = point => () => {
    const { stopIndex, onStopClick } = this.props;

    if (!stopIndex) return;

    if (onStopClick) onStopClick(point);
  };

  renderNode = stopIndex => ({ isHovered }) => (
    <Node stopIndex={stopIndex} isHovered={isHovered} />
  );

  render360 = stopIndex => ({ isHovered }) => (
    <ThreeSixty stopIndex={stopIndex} isHovered={isHovered} />
  );

  render() {
    const { stopIndex, visible, isDesktop } = this.props;
    const { tempStopIndex, pointsHeight } = this.state;

    const nodes = NODES[tempStopIndex] || NODES[-1];
    const threeSixtys = THREE_SIXTYS[tempStopIndex] || THREE_SIXTYS[-1];

    return (
      <div className={styles.container}>
        <div
          className={cx(styles.inner, isDesktop && styles.isDesktop)}
          style={{ height: pointsHeight }}
        >
          {threeSixtys.map((threeSixty, index) => {
            return (
              <StopPoint
                key={`threeSixty-${index}`}
                top={threeSixty.top}
                left={threeSixty.left}
                visible={visible && stopIndex % 1 === 0}
                delayMs={0}
                className={cx(
                  styles.threeSixty,
                  stopPointStyles.threeSixtyAnimation
                )}
                buttonClassName={styles.threeSixtyButton}
                onStopClick={this.handleStopClick(threeSixty)}
                renderChild={this.render360(stopIndex)}
              />
            );
          })}
          {isDesktop
            ? nodes.map((point, index) => {
                const delay = (index + 1) * 120;

                return (
                  <StopPoint
                    key={`node-${index}`}
                    top={point.top}
                    left={point.left}
                    visible={visible && stopIndex % 1 === 0}
                    tooltip={stopIndex ? point.tooltip : null}
                    delayMs={delay}
                    className={cx(styles.node, stopPointStyles.nodeAnimation)}
                    buttonClassName={styles.nodeButton}
                    onStopClick={this.handleStopClick(point)}
                    renderChild={this.renderNode(stopIndex)}
                  />
                );
              })
            : null}
        </div>
      </div>
    );
  }
}

export default VideoStopPoints;
